import RoundedButton from 'components/Atoms/Buttons/RoundedButton/RoundedButton'
import { useTranslation } from 'react-i18next'

interface FinalStepParticularDriverProps {
  days: number
  distance: number
  passenger: number
}

export default function FinalStepParticularDriver({
  days,
  distance,
  passenger,
}: FinalStepParticularDriverProps) {
  const { t } = useTranslation('calculator')

  const averageDistance = distance
  const daysPerYear = days * 52
  const tripsPerDay = 2
  const moneySavedCarpooling = 0.1

  // CO2 factors
  const kgCo2PerEuro = 0.085
  const kgCo2PerTreePerYear = 22 // avg kg CO2 absorbed per tree per year
  const kgCo2PerLiterFuel = 2.3 // avg kg CO2 emitted per liter of fuel

  const amount =
    averageDistance *
    daysPerYear *
    tripsPerDay *
    moneySavedCarpooling *
    passenger

  const roundedAmount = Math.round(amount)

  // CO2 saved in kg
  const co2SavedKg = roundedAmount * kgCo2PerEuro

  const formattedAmount = new Intl.NumberFormat('es-ES', {
    useGrouping: true,
    maximumFractionDigits: 0,
  }).format(roundedAmount)

  const formattedCo2 = new Intl.NumberFormat('es-ES', {
    useGrouping: true,
    maximumFractionDigits: 0,
  }).format(co2SavedKg)

  const formattedTime = new Intl.NumberFormat('es-ES', {
    useGrouping: true,
    maximumFractionDigits: 0,
  }).format(roundedAmount / 8)

  const formattedPerMonth = new Intl.NumberFormat('es-ES', {
    useGrouping: true,
    maximumFractionDigits: 0,
  }).format(roundedAmount / 12)

  // ----- NEW: trees and fuel equivalents -----

  // Trees equivalent (1 tree ≈ 22 kg CO2/year)
  const treesEquivalent = co2SavedKg / kgCo2PerTreePerYear
  const formattedTreesEquivalent = new Intl.NumberFormat('es-ES', {
    useGrouping: true,
    maximumFractionDigits: 0,
  }).format(treesEquivalent)

  // Fuel equivalent in liters (1 L fuel ≈ 2.3 kg CO2)
  const fuelSavedLiters = co2SavedKg / kgCo2PerLiterFuel
  const formattedFuelSavedLiters = new Intl.NumberFormat('es-ES', {
    useGrouping: true,
    maximumFractionDigits: 0,
  }).format(fuelSavedLiters)

  return (
    <div className="h-full flex flex-col justify-between max-sm:gap-6 max-sm:items-center">
      <h4 className="font-bold text-6xl">¡{formattedAmount}€!</h4>
      <div className="flex flex-col gap-3">
        <p>
          {t('calculator.rightColumn.finalStep.particular.result1Month')}{' '}
          <span className="text-blue-800 font-semibold text-xl">
            {formattedPerMonth}€
          </span>
        </p>
        <p>
          {t('calculator.rightColumn.finalStep.particular.result2')}{' '}
          <span className="text-blue-800 font-semibold text-xl">
            {formattedTime}h
          </span>
        </p>
        <p>
          {t('calculator.rightColumn.finalStep.particular.result3')}{' '}
          <span className="text-blue-800 font-semibold text-xl">
            {formattedCo2}kg
          </span>
        </p>
      </div>
      <div className="flex flex-col gap-1 bg-white min-w-56 rounded-xl p-3 w-fit sm:self-start">
        <p>
          {t('calculator.rightColumn.finalStep.particular.equivalence.title')}
        </p>
        <p className="font-semibold">
          {t('calculator.rightColumn.finalStep.particular.equivalence.trees')}
          <span className="text-blue-800 font-semibold text-xl">
            {' '}
            {formattedTreesEquivalent}
          </span>
        </p>
        <p className="font-semibold">
          {t('calculator.rightColumn.finalStep.particular.equivalence.gas')}
          <span className="text-blue-800 font-semibold text-xl">
            {' '}
            {formattedFuelSavedLiters}
          </span>
        </p>
      </div>
      <p className="font-semibold">
        {t('calculator.rightColumn.finalStep.particular.text')} "
        <span className="text-blue-800">ciclofriends</span>"
      </p>
      <a href="https://www.ciclogreen.com/qr">
        <RoundedButton>
          {t('calculator.rightColumn.finalStep.particular.cta')}
        </RoundedButton>
      </a>
    </div>
  )
}
